import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AccountRequest as AuthorizationAccountRequest, AccountResponse as AuthorizationAccount, AuthorizationAccountApi, AuthType } from '@venecia/apis/auth';
import { Account, ProfileApi, User, UserApi, UserRequest, PersonApi, Person } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, LanguageService, MessageService, Language, TraceService } from '@venecia/services';
import { UrlUtil } from '@venecia/utils';

@Component({
  selector: 'app-user-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './user-modal.html',
  styleUrl: './user-modal.scss',
})
export class UserModal implements OnChanges {
  private readonly personApi = inject(PersonApi);
  private readonly authorizationAccountApi = inject(AuthorizationAccountApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly languageService = inject(LanguageService);
  private readonly messageService = inject(MessageService);
  private readonly profileApi = inject(ProfileApi);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);
  private readonly userApi = inject(UserApi);

  @Input() open = false;
  @Input() preselectedAccountId: number | null = null;
  @Input() user: User | null = null;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly availableAccounts = signal<readonly Account[]>([]);
  readonly availableProfiles = signal<readonly SelectableProfile[]>([]);
  readonly authorizationAccount = signal<AuthorizationAccount | null>(null);
  readonly authorizationAccountBusy = signal(false);
  readonly authorizationAccountLookupState = signal<AuthorizationAccountLookupState>('idle');
  readonly authorizationAccountLanguage = signal<string>('es');
  readonly authorizationAccountType = signal<AuthType>('INTERNAL');
  readonly activeTab = signal<'authorization' | 'general'>('general');
  readonly searchedPerson = signal(true);
  readonly deleting = signal(false);
  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly profiles = signal<readonly SelectableProfile[]>([]);
  readonly saving = signal(false);
  readonly searching = signal(false);
  readonly personRef = signal<string | null>(null);
  readonly selectedAccountId = signal<number | null>(null);
  readonly selectedProfileId = signal<number | null>(null);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');
  readonly hasAuthorizationAccount = computed(() => this.authorizationAccountLookupState() === 'loaded' && this.authorizationAccount() !== null);
  readonly isAuthorizationAccountInternal = computed(() => this.authorizationAccount()?.['auth-type'] === 'INTERNAL');
  readonly isAuthorizationOtpEnabled = computed(() => Boolean(this.authorizationAccount()?.['otp-enabled-at']));
  readonly authorizationAccountStatusKey = computed(() => {
    switch (this.authorizationAccountLookupState()) {
      case 'loaded':
        return 'pages.users.authorization-account.states.active';
      case 'loading':
        return 'pages.users.authorization-account.states.loading';
      case 'missing':
        return 'pages.users.authorization-account.states.inactive';
      case 'error':
        return 'pages.users.authorization-account.states.unavailable';
      default:
        return 'pages.users.authorization-account.states.unavailable';
    }
  });
  readonly authorizationAccountStatusClass = computed(() => {
    switch (this.authorizationAccountLookupState()) {
      case 'loaded':
        return 'auth-account-status auth-account-status-active';
      case 'loading':
        return 'auth-account-status auth-account-status-loading';
      case 'missing':
        return 'auth-account-status auth-account-status-inactive';
      default:
        return 'auth-account-status auth-account-status-unavailable';
    }
  });
  readonly selectableProfiles = computed(() => {
    const selectedIds = new Set(
      this.profiles()
        .map((profile) => profile.id)
        .filter((profileId): profileId is number => profileId !== null),
    );

    return this.availableProfiles().filter(
      (profile) =>
        profile.id !== null &&
        !selectedIds.has(profile.id) &&
        (this.selectedAccountId() === null || profile.account?.id === this.selectedAccountId()),
    );
  });
  readonly supportedLanguages: readonly Language[] = this.languageService.getSupportedLanguages();
  readonly supportedAuthTypes: readonly AuthType[] = ['INTERNAL', 'OIDC'];

  readonly form = this.formBuilder.nonNullable.group({
    avatar: ['', [Validators.maxLength(1024), UrlUtil.urlValidator()]],
    contact: [false],
    firstName: ['', [Validators.required, Validators.maxLength(256)]],
    lastName: ['', [Validators.required, Validators.maxLength(256)]],
    phone: ['', [Validators.required, Validators.maxLength(32)]],
    username: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['user'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && (this.saving() || this.deleting() || this.authorizationAccountBusy())) {
      return;
    }

    this.closed.emit();
  }

  async requestDelete(): Promise<void> {
    const id = this.selectedId();
    if (id === null) {
      return;
    }

    const keys = [
      'pages.users.confirm-delete.cancel',
      'pages.users.confirm-delete.confirm',
      'pages.users.confirm-delete.message',
      'pages.users.confirm-delete.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    const confirmed = await this.confirmDialogService.open({
      cancelText: i18n['pages.users.confirm-delete.cancel'],
      confirmText: i18n['pages.users.confirm-delete.confirm'],
      message: i18n['pages.users.confirm-delete.message'],
      title: i18n['pages.users.confirm-delete.title'],
    });

    if (!confirmed) {
      return;
    }

    this.deleting.set(true);
    try {
      await firstValueFrom(this.userApi.delete(id));

      if (this.authorizationAccount()?.id !== undefined) {
        await firstValueFrom(this.authorizationAccountApi.delete(this.authorizationAccount()!.id));
      }

      this.messageService.success(await this.translate('pages.users.messages.delete-success'));
      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.delete-error'));
    } finally {
      this.deleting.set(false);
    }
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      const createdOrUpdatedUser = this.isCreateMode()
        ? (await firstValueFrom(this.userApi.create(payload))).body
        : (await firstValueFrom(this.userApi.update(this.selectedIdOrThrow(), payload))).body;

      this.selectedId.set(createdOrUpdatedUser.id);
      await this.persistProfilesOnSave();

      if (this.isCreateMode()) {
        this.messageService.success(await this.translate('pages.users.messages.create-success'));
      } else {
        this.messageService.success(await this.translate('pages.users.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  hasError(controlName: keyof UserFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  addSelectedProfile(): void {
    const selectedProfileId = this.selectedProfileId();
    if (selectedProfileId === null) {
      return;
    }

    const profileToAdd = this.availableProfiles().find((profile) => profile.id === selectedProfileId);
    if (!profileToAdd) {
      return;
    }

    if (this.profiles().some(profile => profile.application?.id === profileToAdd.application?.id)) {
      this.messageService.warning("Ya tiene un perfil en la misma aplicación");
      return;
    }

    const alreadySelected = this.profiles().some((profile) => profile.id === selectedProfileId);
    if (alreadySelected) {
      return;
    }

    this.profiles.set([...this.profiles(), profileToAdd]);
    this.selectedProfileId.set(null);
  }

  isInvalid(controlName: keyof UserFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  onAccountSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    const accountId = value ? Number(value) : null;

    this.selectedAccountId.set(accountId);
    this.profiles.set(
      this.profiles().filter(
        (profile) => accountId === null || profile.account?.id === accountId,
      ),
    );
  }

  onProfileSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.selectedProfileId.set(value ? Number(value) : null);
  }

  removeProfile(profileId: number): void {
    const updatedProfiles = this.profiles().filter((profile) => profile.id !== profileId);
    this.profiles.set(updatedProfiles);
  }

  selectTab(tab: 'authorization' | 'general'): void {
    this.activeTab.set(tab);
  }

  onAuthorizationAccountTypeChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value === 'OIDC' ? 'OIDC' : 'INTERNAL';
    this.authorizationAccountType.set(value);
  }

  onAuthorizationLanguageChange(event: Event): void {
    const target = event.target as HTMLSelectElement | null;
    const value = target?.value ?? '';
    this.authorizationAccountLanguage.set(this.resolveSupportedLanguage(value));

    if (this.hasAuthorizationAccount()
      && !(this.saving()
        || this.deleting()
        || this.authorizationAccountBusy()
        || this.authorizationAccount()?.['preferred-language'] === this.authorizationAccountLanguage())) {
      this.updateAuthorizationAccountLanguage();
    }
  }

  async createAuthorizationAccount(): Promise<void> {
    const username = this.currentUsername();
    if (!username) {
      return;
    }

    const payload: AuthorizationAccountRequest = {
      username,
      'auth-type': this.authorizationAccountType(),
      'preferred-language': this.authorizationAccountLanguage(),
    };

    this.authorizationAccountBusy.set(true);
    try {
      const response = await firstValueFrom(this.authorizationAccountApi.createAccount(payload));
      this.applyAuthorizationAccount(response.body);
      this.messageService.success(await this.translate('pages.users.messages.authorization-account-create-success'));
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-create-error'));
    } finally {
      this.authorizationAccountBusy.set(false);
    }
  }

  async updateAuthorizationAccountLanguage(): Promise<void> {
    const account = this.authorizationAccount();
    if (!account || account['preferred-language'] === this.authorizationAccountLanguage()) {
      return;
    }

    this.authorizationAccountBusy.set(true);
    try {
      await firstValueFrom(
        this.authorizationAccountApi.updateAccountLanguage(account.id, this.authorizationAccountLanguage()),
      );
      this.authorizationAccount.set({
        ...account,
        'preferred-language': this.authorizationAccountLanguage(),
      });
      this.messageService.success(await this.translate('pages.users.messages.authorization-account-language-success'));
    } catch (error) {
      this.traceService.internal(error, true);
      this.authorizationAccountLanguage.set(this.resolveSupportedLanguage(account['preferred-language']));
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-language-error'));
    } finally {
      this.authorizationAccountBusy.set(false);
    }
  }

  async resetAuthorizationAccount(): Promise<void> {
    const account = this.authorizationAccount();
    if (!account) {
      return;
    }

    this.authorizationAccountBusy.set(true);
    try {
      await firstValueFrom(this.authorizationAccountApi.resetAccount(account.id));
      this.authorizationAccount.set({
        ...account,
        'access-blocked-at': undefined,
        'failed-attempts': 0,
        'last-failed-attempt-at': undefined,
      });
      this.messageService.success(await this.translate('pages.users.messages.authorization-account-reset-success'));
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-reset-error'));
    } finally {
      this.authorizationAccountBusy.set(false);
    }
  }

  async requestAuthorizationAccountPasswordChange(): Promise<void> {
    const account = this.authorizationAccount();
    if (!account) {
      return;
    }

    this.authorizationAccountBusy.set(true);
    try {
      await firstValueFrom(this.authorizationAccountApi.requestAccountPasswordChange(account.id));
      this.authorizationAccount.set({
        ...account,
        'change-password': true,
      });
      this.messageService.success(await this.translate('pages.users.messages.authorization-account-password-success'));
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-password-error'));
    } finally {
      this.authorizationAccountBusy.set(false);
    }
  }

  async onAuthorizationOtpChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const checked = input?.checked ?? false;
    const account = this.authorizationAccount();

    if (!account || !checked || this.isAuthorizationOtpEnabled()) {
      if (input) {
        input.checked = this.isAuthorizationOtpEnabled();
      }
      return;
    }

    this.authorizationAccountBusy.set(true);
    try {
      await firstValueFrom(this.authorizationAccountApi.enableAccountOtp(account.id));
      this.authorizationAccount.set({
        ...account,
        'otp-enabled-at': account['otp-enabled-at'] ?? new Date().toISOString(),
      });
      this.messageService.success(await this.translate('pages.users.messages.authorization-account-otp-success'));
    } catch (error) {
      this.traceService.internal(error, true);
      if (input) {
        input.checked = false;
      }
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-otp-error'));
    } finally {
      this.authorizationAccountBusy.set(false);
    }
  }

  private configureFromInputs(): void {
    this.submitted.set(false);
    this.activeTab.set('general');
    this.resetAuthorizationAccountState();

    const user = this.user;
    if (!user) {
      this.availableAccounts.set([]);
      this.availableProfiles.set([]);
      this.detailMode.set('create');
      this.personRef.set(null);
      this.selectedAccountId.set(this.preselectedAccountId);
      this.selectedId.set(null);
      this.selectedCreatedAt.set(null);
      this.selectedProfileId.set(null);
      this.selectedUpdatedAt.set(null);
      this.profiles.set([]);
      this.searchedPerson.set(false);
      this.searching.set(false);

      this.form.reset({
        avatar: '',
        contact: false,
        firstName: '',
        lastName: '',
        phone: '',
        username: '',
      });
      void this.loadProfiles();
      return;
    }

    this.detailMode.set('edit');
    this.personRef.set(user.person['person-ref'] ?? null);
    this.selectedAccountId.set(user.account?.id ?? null);
    this.selectedId.set(user.id);
    this.selectedCreatedAt.set(user['created-at'] ?? null);
    this.selectedUpdatedAt.set(user['updated-at'] ?? null);
    this.profiles.set(user.profiles ?? []);
    this.selectedProfileId.set(null);

    this.form.reset({
      avatar: user['avatar-url'] ?? '',
      contact: user.contact ?? false,
      firstName: user.person['first-name'],
      lastName: user.person['last-name'],
      phone: user.person.phone,
      username: user.username,
    });

    void this.loadProfiles();
    void this.loadAuthorizationAccountByUsername(user.username);
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('User id is required for update');
    }
    return id;
  }

  private toApiModel(): UserRequest {
    const formValue = this.form.getRawValue();
    const accountId = this.selectedAccountId();
    if (accountId === null) {
      throw new Error('Account id is required for user create/update');
    }

    return {
      account: { id: accountId },
      'avatar-url': formValue.avatar.trim() || undefined,
      contact: formValue.contact,
      username: formValue.username.trim(),
      person: {
        'person-ref': this.personRef() ?? undefined,
        'first-name': formValue.firstName.trim(),
        'last-name': formValue.lastName.trim(),
        phone: formValue.phone.trim(),
      },
    };
  }

  private async loadProfiles(): Promise<void> {
    try {
      const response = await firstValueFrom(this.profileApi.search());
      this.availableProfiles.set(response.body.data ?? []);
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.profiles-load-error'));
    }
  }

  private async loadAuthorizationAccountByUsername(username?: string): Promise<void> {
    const normalizedUsername = username?.trim() ?? '';
    if (!normalizedUsername) {
      this.authorizationAccountLookupState.set('missing');
      return;
    }

    this.authorizationAccountLookupState.set('loading');

    try {
      const response = await firstValueFrom(this.authorizationAccountApi.findAccountByUsername(normalizedUsername));

      if (this.currentUsername() !== normalizedUsername) {
        return;
      }

      this.applyAuthorizationAccount(response.body);
    } catch (error) {
      if (this.currentUsername() !== normalizedUsername) {
        return;
      }

      if (this.isNotFoundError(error)) {
        this.authorizationAccount.set(null);
        this.authorizationAccountLookupState.set('missing');
        this.authorizationAccountType.set('INTERNAL');
        this.authorizationAccountLanguage.set(this.resolveDefaultAuthorizationLanguage());
        return;
      }

      this.authorizationAccount.set(null);
      this.authorizationAccountLookupState.set('error');
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.users.authorization-account-load-error'));
    }
  }

  private toProfileIdPayload(profiles: readonly SelectableProfile[]): readonly { readonly id: number }[] {
    return profiles
      .map((profile) => profile.id)
      .filter((profileId): profileId is number => profileId !== null)
      .map((profileId) => ({ id: profileId }));
  }

  private async persistProfilesOnSave(): Promise<void> {
    const userId = this.selectedId();
    if (userId === null) {
      return;
    }

    await firstValueFrom(this.userApi.updateProfiles(userId, this.toProfileIdPayload(this.profiles())));
  }

  private applyAuthorizationAccount(account: AuthorizationAccount): void {
    this.authorizationAccount.set(account);
    this.authorizationAccountLookupState.set('loaded');
    this.authorizationAccountType.set(account['auth-type']);
    this.authorizationAccountLanguage.set(this.resolveSupportedLanguage(account['preferred-language']));
  }

  private resetAuthorizationAccountState(): void {
    this.authorizationAccount.set(null);
    this.authorizationAccountBusy.set(false);
    this.authorizationAccountLookupState.set('idle');
    this.authorizationAccountType.set('INTERNAL');
    this.authorizationAccountLanguage.set(this.resolveDefaultAuthorizationLanguage());
  }

  currentUsername(): string | null {
    const username = this.form.controls.username.value.trim();
    return username ? username : null;
  }

  async searchPerson(): Promise<void> {
    if (this.preselectedAccountId !== null && this.currentUsername() !== null && this.form.controls.username.valid) {
      this.searching.set(true);
      try {
        const username = this.currentUsername() ?? "";
        const response = await firstValueFrom(this.personApi.findPersonByUsername(this.preselectedAccountId, username));
        if (!response.error && response.body && response.body['person-ref']) {
          this.personRef.set(response.body['person-ref']);
          this.form.reset({
            username: username,
            firstName: response.body['first-name'],
            lastName: response.body['last-name'],
            phone: response.body['phone'],
          });
          this.messageService.success(await this.translate("pages.users.messages.search-success"));
        } else {
          this.messageService.info(await this.translate("pages.users.messages.search-empty"));
        }
      } catch (error) {
        this.traceService.internal(error, true);
        this.messageService.warning(await this.errorMessageService.resolveApiError(error, 'pages.users.person-load-error'));
      }
      this.searchedPerson.set(true);
      this.searching.set(false);
    }
    this.form.controls.username.markAsTouched();
  }

  private resolveDefaultAuthorizationLanguage(): string {
    const currentLanguage = (this.translateService.currentLang || this.translateService.getDefaultLang() || '').toLowerCase();
    return this.resolveSupportedLanguage(currentLanguage);
  }

  private resolveSupportedLanguage(value: string): string {
    return this.languageService.isSupportedLanguage(value) ? value : this.supportedLanguages[0]?.code ?? 'es';
  }

  private isNotFoundError(error: unknown): boolean {
    return typeof error === 'object'
      && error !== null
      && 'status' in error
      && (error as { status?: unknown }).status === 404;
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface UserFormModel {
  avatar: string;
  contact: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  username: string;
}

type SelectableProfile = {
  id: number | null;
  name: string;
  account?: Account;
  application?: {
    id: number | null;
    code: string;
    name: string;
    'site-url': string;
    'logo-url'?: string;
    readonly key?: string;
    readonly 'created-at'?: string;
    readonly 'updated-at'?: string;
  };
};

type AuthorizationAccountLookupState = 'idle' | 'loading' | 'loaded' | 'missing' | 'error';
